import { Router } from "@frui.ts/screens/dist";
import { Modules, newNavigationName } from "config";
import { differenceInCalendarDays } from "date-fns";
import { getValidationMessage } from "@frui.ts/validation";
import { computed } from "mobx";
import UserRepository from "repositories/userRepository";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import EventBus from "services/eventBus";
import addDays from "date-fns/addDays";
import OptionsService from "services/optionsService";
import { InternalUserValidationRules } from "entities/user";
import ExternalUser, { ExternalUserValidationRules } from "entities/externalUser";
import { endOfCzechDay } from "utils/dateUtils";
import ExternalIdentityProvider from "../../../entities/externalIdentityProvider";
import { attachAutomaticValidator } from "../../../utils/validatorAggregator";
import CreateViewModel from "../../createViewModel";
import AppRouter from "services/router";
import UserDetailViewModel from "./userDetailViewModel";

@Router.registerRoute({ route: newNavigationName, name: Router.Self })
export default class UserCreateViewModel extends CreateViewModel<ExternalUser> {
  readonly modelName = "user";

  constructor(
    protected repository: UserRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    private optionsService: OptionsService,
    private appRouter: AppRouter
  ) {
    super();
    this.navigationName = newNavigationName;
  }

  protected loadDetail() {
    const item = new ExternalUser();
    item.invitationValidUntil = addDays(endOfCzechDay(new Date()), 3);
    attachAutomaticValidator(item, InternalUserValidationRules, false, () => !this.hasExternalProviders);
    attachAutomaticValidator(item, ExternalUserValidationRules, false, () => this.hasExternalProviders);
    return item;
  }

  protected async postData() {
    if (!this.hasExternalProviders) {
      const data = await super.postData();
      this.notificationService.add({ title: this.translate("created"), type: NotificationType.SUCCESS });
      return await this.appRouter.navigate(UserDetailViewModel, { userId: data.id });
    } else {
      const data = await this.repository.createExternal(this.item);
      this.notificationService.add({ title: this.translate("created"), type: NotificationType.SUCCESS });
      return await this.appRouter.navigate(UserDetailViewModel, { userId: data.id });
    }
  }

  @computed
  get externalIdentityProviderOptions() {
    return this.optionsService.entityOptions(ExternalIdentityProvider);
  }

  @computed
  get hasExternalProviders() {
    return this.item && this.item.allowedProviders.length > 0;
  }

  set invitationDays(value: number | string) {
    if (value) {
      const days = parseInt(`${value}`);
      this.item.invitationValidUntil = addDays(endOfCzechDay(new Date()), days);
    } else {
      this.item.invitationValidUntil = undefined;
    }
  }

  @computed
  get invitationDays() {
    return this.item.invitationValidUntil ? differenceInCalendarDays(this.item.invitationValidUntil, new Date()) : "";
  }

  @computed
  get invitationDaysError() {
    return getValidationMessage(this.item, "invitationValidUntil");
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `users.${code}`, size);
  }
}
